import { useState } from "react";

import { styled, useTheme } from "styled-components";

import { Divider, Dropdown, Icon, Popup } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import type { CompanyShareholder } from "components/innointel/types";

import {
  InfoBoxWidget,
  InfoBoxWidgetTitleArea,
  InfoContainer,
  InfoItem,
} from "../Styles";

const StyledShareholder = styled.div<{ $isVC?: boolean; $isCompany?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  border-radius: 5px;
  padding: 2.5px;
  ${({ $isVC }) =>
    $isVC &&
    `
    background-color: #ffdd99
  `}
  ${({ $isCompany, $isVC }) =>
    $isCompany &&
    `
    cursor: pointer;
    &:hover {
      background-color: ${$isVC ? "#ffc34d" : "#e9ebee"};
    }
  `}
  span {
    display: block;
    margin: 2.5px 0;
  }
  .type {
    color: gray;
    font-weight: bold;
  }
  .name {
    font-size: 1.2em;
  }
  .meta {
    color: gray;
  }
`;

const displayOptions = [
  { key: "company", text: "Companies", value: "company" },
  { key: "person", text: "People", value: "person" },
  { key: "other", text: "Other", value: "other" },
];

const ventureCapitalWords = ["vc", "capital", "bank", "venture", "ventures"];

const isVC = (companyName: string) => {
  const splitCompanyName = companyName.toLowerCase().split(" ");
  return (
    splitCompanyName.filter((word) => ventureCapitalWords.indexOf(word) > -1)
      .length > 0
  );
};

const sortedShareholders = (
  sortType: string,
  shareholders: CompanyShareholder[],
) => {
  const sorted = [...shareholders];
  switch (sortType) {
    case "sharesDesc":
      return sorted.sort(
        (a, b) => b.totalNumberOfSharesOwned - a.totalNumberOfSharesOwned,
      );
    case "sharesAsc":
      return sorted.sort(
        (a, b) => a.totalNumberOfSharesOwned - b.totalNumberOfSharesOwned,
      );
    default:
      return sorted.sort(
        (a, b) => b.totalNumberOfSharesOwned - a.totalNumberOfSharesOwned,
      );
  }
};

type CompanyShareholdersProps = {
  shareholders: CompanyShareholder[];
  isPDF?: boolean;
};

const CompanyShareholders = ({
  shareholders,
  isPDF,
}: CompanyShareholdersProps) => {
  const [sortType, setSortType] = useState("sharesDesc");
  const [show, setShow] = useState(["company", "person", "other"]);
  const [filter, setFilter] = useState("");
  const theme = useTheme();

  if (shareholders.length) {
    return (
      <InfoBoxWidget
        $noBox
        style={
          !isPDF ? { maxHeight: 520, minHeight: 200, overflow: "auto" } : {}
        }
      >
        <InfoBoxWidgetTitleArea>
          <Icon name="group" />
          <div>
            <h3>Shareholders</h3>
            <span>{shareholders.length} shareholder(s) found</span>
          </div>
        </InfoBoxWidgetTitleArea>
        <Divider hidden />
        {!isPDF ?
          <InfoBoxWidgetTitleArea $alignItems="center" $flexDirection="row">
            <Input
              placeholder="Search shareholders..."
              value={filter}
              onChange={(_e, { value }) => setFilter(value ?? "")}
              style={theme.sizes.isMobile ? { width: "100%" } : { width: 300 }}
            />
            <Dropdown
              selection
              placeholder="Choose shareholder sort..."
              defaultValue="sharesDesc"
              options={[
                {
                  key: "sharesDesc",
                  value: "sharesDesc",
                  text: "Total number of shares held (Desc)",
                },
                {
                  key: "sharesAsc",
                  value: "sharesAsc",
                  text: "Total number of shares held (Asc)",
                },
              ]}
              value={sortType}
              onChange={(_e, { value }) => setSortType(String(value))}
              size="tiny"
              style={theme.sizes.isMobile ? { width: "100%" } : { width: 300 }}
            />
            <Dropdown
              selection
              multiple
              placeholder="Display shareholder type.."
              options={displayOptions}
              value={show}
              onChange={(_e, { value }) => {
                if (Array.isArray(value)) {
                  setShow(value.map(String));
                }
              }}
              size="tiny"
              style={theme.sizes.isMobile ? { width: "100%" } : { width: 350 }}
            />
          </InfoBoxWidgetTitleArea>
        : null}

        <InfoContainer $wrapped>
          {sortedShareholders(sortType, shareholders)
            .filter(
              (shareholder) =>
                shareholder.name.toLowerCase().indexOf(filter.toLowerCase()) >
                -1,
            )
            .map((shareholder) => {
              const isShown =
                show.indexOf(shareholder.shareholderType.toLowerCase()) > -1;
              const isCompany =
                shareholder.shareholderType.toLowerCase() === "company";
              const assumedVC = isVC(shareholder.name);

              if (!isShown) return null;
              return (
                <Popup
                  key={shareholder.name}
                  disabled={!assumedVC}
                  on="hover"
                  content="Company is assumed to be venture capital"
                  trigger={
                    <InfoItem $compact>
                      <StyledShareholder
                        $isVC={assumedVC}
                        $isCompany={isCompany}
                      >
                        <span className="type">
                          {shareholder.shareholderType.toUpperCase()}
                        </span>
                        <span className="main">{shareholder.name}</span>
                        <span className="meta">
                          Shares: {shareholder.totalNumberOfSharesOwned},{" "}
                          {shareholder.percentSharesHeld || "<0.01"}%,{" "}
                          {shareholder.shareType}
                        </span>
                      </StyledShareholder>
                    </InfoItem>
                  }
                />
              );
            })}
        </InfoContainer>
      </InfoBoxWidget>
    );
  }
  return null;
};

export default CompanyShareholders;
